"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { useAuth } from "@/contexts/AuthContext";
import { usePlannerEvent } from "@/contexts/PlannerEventContext";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Edit, Save, Download } from "lucide-react";
import {
  fixSerbianText,
  drawPDFHeader,
  drawPDFFooter,
  drawStatRow,
  getPDFTableStyles,
  withPDFRelaxedRows,
  loadPDFLogo,
  loadPDF,
  C,
  MARGIN,
} from "@/lib/pdf-utils";
import { getBudget, saveBudget, type Budget, type Expense } from "@/lib/api";
import { PlannerEventRequiredNotice } from "@/components/PlannerEventRequiredNotice";
import { EventDataLoading } from "@/components/EventDataLoading";

export default function KalukatorBudzetaPage() {
  const { user, token, isLoading: authLoading } = useAuth();
  const {
    activeEventNumericId,
    listReady: plannerListReady,
    summaries: plannerSummaries,
  } = usePlannerEvent();
  const hasNoEvents = plannerListReady && plannerSummaries.length === 0;
  const activeEventNumericIdRef = useRef(activeEventNumericId);
  useEffect(() => {
    activeEventNumericIdRef.current = activeEventNumericId;
  }, [activeEventNumericId]);

  const router = useRouter();
  const [budget, setBudget] = useState<Budget | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [budgetAmount, setBudgetAmount] = useState<string>("");
  const [expenses, setExpenses] = useState<Expense[]>([]);

  // Editing expense state
  const [isEditExpenseDialogOpen, setIsEditExpenseDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [editingExpenseIndex, setEditingExpenseIndex] = useState<number | null>(
    null
  );

  // Create expense dialog state
  const [isCreateExpenseDialogOpen, setIsCreateExpenseDialogOpen] =
    useState(false);

  // New expense form state (Dialog only)
  const [newExpense, setNewExpense] = useState<Expense>({
    name: "",
    amount: 0,
    paid: false,
  });
  const [expenseNameError, setExpenseNameError] = useState<string | null>(null);
  const [expenseAmountError, setExpenseAmountError] = useState<string | null>(
    null
  );

  // Card "Dodaj trošak" form state (independent from Dialog)
  const [cardExpense, setCardExpense] = useState<Expense>({
    name: "",
    amount: 0,
    paid: false,
  });
  const [cardExpenseNameError, setCardExpenseNameError] = useState<
    string | null
  >(null);
  const [cardExpenseAmountError, setCardExpenseAmountError] = useState<
    string | null
  >(null);

  // Delete expense state
  const [deleteExpenseIndex, setDeleteExpenseIndex] = useState<number | null>(
    null
  );

  // Sort state
  const [sortBy, setSortBy] = useState<string>("none");

  useEffect(() => {
    if (!authLoading && !user) {
      router.push("/prijava");
    }
  }, [user, authLoading, router]);

  useEffect(() => {
    if (user && token && plannerListReady && activeEventNumericId != null) {
      setLoading(true);
      setBudget(null);
      setBudgetAmount("");
      setExpenses([]);
      setError(null);
      void loadBudget();
    } else if (
      user &&
      token &&
      plannerListReady &&
      activeEventNumericId == null
    ) {
      setBudget(null);
      setBudgetAmount("");
      setExpenses([]);
      setLoading(false);
    }
  }, [user, token, plannerListReady, activeEventNumericId]);

  const loadBudget = async () => {
    if (!token || !user) return;
    const forEvent = activeEventNumericIdRef.current;
    if (forEvent == null) return;

    try {
      setLoading(true);
      setError(null);
      const budgetData = await getBudget(token, user.id, forEvent);

      if (activeEventNumericIdRef.current !== forEvent) return;

      if (budgetData) {
        setBudget(budgetData);
        setBudgetAmount(budgetData.budget.toString());
        setExpenses(budgetData.expense || []);
      } else {
        setBudget(null);
        setBudgetAmount("");
        setExpenses([]);
      }
    } catch (err) {
      console.error("Error loading budget:", err);
      if (activeEventNumericIdRef.current === forEvent) {
        setError(
          err instanceof Error ? err.message : "Greška pri učitavanju budžeta"
        );
      }
    } finally {
      if (activeEventNumericIdRef.current === forEvent) {
        setLoading(false);
      }
    }
  };

  const handleSaveBudget = async () => {
    if (!token || !user || hasNoEvents) return;

    const budgetValue = parseFloat(budgetAmount) || 0;

    try {
      setSaving(true);
      setError(null);

      const savedBudget = await saveBudget(
        token,
        budgetValue,
        expenses,
        user.id,
        budget?.id,
        budget?.documentId,
        activeEventNumericId ?? undefined
      );

      setBudget(savedBudget);
      setError(null);
    } catch (err) {
      console.error("Error saving budget:", err);
      setError(
        err instanceof Error ? err.message : "Greška pri čuvanju budžeta"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleAddExpense = async (): Promise<boolean> => {
    if (hasNoEvents) return false;
    const nameMissing = !newExpense.name.trim();
    const amountMissing = newExpense.amount <= 0;
    if (nameMissing) setExpenseNameError("Naziv je obavezan");
    else setExpenseNameError(null);
    if (amountMissing) setExpenseAmountError("Iznos je obavezan");
    else setExpenseAmountError(null);
    if (nameMissing || amountMissing) return false;

    const expense: Expense = {
      name: newExpense.name.trim(),
      amount: parseFloat(newExpense.amount.toString()) || 0,
      paid: newExpense.paid || false,
    };

    const updatedExpenses = [expense, ...expenses];
    setExpenses(updatedExpenses);
    setNewExpense({
      name: "",
      amount: 0,
      paid: false,
    });
    setError(null);
    setExpenseNameError(null);
    setExpenseAmountError(null);

    // Auto-save after adding expense
    if (token && user) {
      try {
        const budgetValue = parseFloat(budgetAmount) || 0;
        const savedBudget = await saveBudget(
          token,
          budgetValue,
          updatedExpenses,
          user.id,
          budget?.id,
          budget?.documentId,
          activeEventNumericId ?? undefined
        );
        setBudget(savedBudget);
      } catch (err) {
        console.error("Error auto-saving after adding expense:", err);
        // Don't show error to user for auto-save, just log it
      }
    }
    return true;
  };

  const handleAddExpenseFromCard = async () => {
    if (hasNoEvents) return;
    const nameMissing = !cardExpense.name.trim();
    const amountMissing = cardExpense.amount <= 0;
    if (nameMissing) setCardExpenseNameError("Naziv je obavezan");
    else setCardExpenseNameError(null);
    if (amountMissing) setCardExpenseAmountError("Iznos je obavezan");
    else setCardExpenseAmountError(null);
    if (nameMissing || amountMissing) return;

    const expense: Expense = {
      name: cardExpense.name.trim(),
      amount: parseFloat(cardExpense.amount.toString()) || 0,
      paid: cardExpense.paid || false,
    };

    const updatedExpenses = [expense, ...expenses];
    setExpenses(updatedExpenses);
    setCardExpense({ name: "", amount: 0, paid: false });
    setCardExpenseNameError(null);
    setCardExpenseAmountError(null);
    setError(null);

    if (token && user) {
      try {
        const budgetValue = parseFloat(budgetAmount) || 0;
        const savedBudget = await saveBudget(
          token,
          budgetValue,
          updatedExpenses,
          user.id,
          budget?.id,
          budget?.documentId,
          activeEventNumericId ?? undefined
        );
        setBudget(savedBudget);
      } catch (err) {
        console.error("Error auto-saving after adding expense:", err);
      }
    }
  };

  const handleEditExpense = (index: number) => {
    if (hasNoEvents) return;
    const expense = expenses[index];
    setEditingExpense({ ...expense });
    setEditingExpenseIndex(index);
    setExpenseNameError(null);
    setExpenseAmountError(null);
    setIsEditExpenseDialogOpen(true);
  };

  const handleSaveEditedExpense = async () => {
    if (hasNoEvents || !editingExpense || editingExpenseIndex === null) return;
    const nameMissing = !editingExpense.name.trim();
    const amountMissing = editingExpense.amount <= 0;
    if (nameMissing) setExpenseNameError("Naziv je obavezan");
    else setExpenseNameError(null);
    if (amountMissing) setExpenseAmountError("Iznos je obavezan");
    else setExpenseAmountError(null);
    if (nameMissing || amountMissing) return;

    const updatedExpenses = [...expenses];
    updatedExpenses[editingExpenseIndex] = {
      name: editingExpense.name.trim(),
      amount: parseFloat(editingExpense.amount.toString()) || 0,
      paid: editingExpense.paid || false,
    };
    setExpenses(updatedExpenses);
    setIsEditExpenseDialogOpen(false);
    setEditingExpense(null);
    setEditingExpenseIndex(null);
    setError(null);
    setExpenseNameError(null);
    setExpenseAmountError(null);

    // Auto-save after editing expense
    if (token && user) {
      try {
        const budgetValue = parseFloat(budgetAmount) || 0;
        const savedBudget = await saveBudget(
          token,
          budgetValue,
          updatedExpenses,
          user.id,
          budget?.id,
          budget?.documentId,
          activeEventNumericId ?? undefined
        );
        setBudget(savedBudget);
      } catch (err) {
        console.error("Error auto-saving after editing expense:", err);
        // Don't show error to user for auto-save, just log it
      }
    }
  };

  const handleCancelEditExpense = () => {
    setIsEditExpenseDialogOpen(false);
    setEditingExpense(null);
    setEditingExpenseIndex(null);
  };

  const handleDeleteExpense = async (index: number) => {
    if (hasNoEvents) return;
    const updatedExpenses = expenses.filter((_, i) => i !== index);
    setExpenses(updatedExpenses);
    setDeleteExpenseIndex(null);

    // Auto-save after deleting expense
    if (token && user) {
      try {
        const budgetValue = parseFloat(budgetAmount) || 0;
        const savedBudget = await saveBudget(
          token,
          budgetValue,
          updatedExpenses,
          user.id,
          budget?.id,
          budget?.documentId,
          activeEventNumericId ?? undefined
        );
        setBudget(savedBudget);
      } catch (err) {
        console.error("Error auto-saving after deleting expense:", err);
        // Don't show error to user for auto-save, just log it
      }
    }
  };

  const toggleExpensePaid = async (index: number) => {
    if (hasNoEvents) return;
    const updatedExpenses = [...expenses];
    updatedExpenses[index] = {
      ...updatedExpenses[index],
      paid: !updatedExpenses[index].paid,
    };
    setExpenses(updatedExpenses);

    // Auto-save after toggling paid status
    if (token && user) {
      try {
        const budgetValue = parseFloat(budgetAmount) || 0;
        const savedBudget = await saveBudget(
          token,
          budgetValue,
          updatedExpenses,
          user.id,
          budget?.id,
          budget?.documentId,
          activeEventNumericId ?? undefined
        );
        setBudget(savedBudget);
      } catch (err) {
        console.error("Error auto-saving after toggling paid status:", err);
        // Don't show error to user for auto-save, just log it
      }
    }
  };

  // Calculate totals
  const totalExpenses = expenses.reduce((sum, exp) => sum + exp.amount, 0);
  const totalPaid = expenses
    .filter((exp) => exp.paid)
    .reduce((sum, exp) => sum + exp.amount, 0);
  const totalUnpaid = expenses
    .filter((exp) => !exp.paid)
    .reduce((sum, exp) => sum + exp.amount, 0);
  const budgetValue = parseFloat(budgetAmount) || 0;
  const remainingBudget = budgetValue - totalExpenses;

  // Sort expenses with original index tracking
  const getSortedExpenses = (): Array<{
    expense: Expense;
    originalIndex: number;
  }> => {
    const expensesWithIndex = expenses.map((exp, index) => ({
      expense: exp,
      originalIndex: index,
    }));

    switch (sortBy) {
      case "highest":
        return expensesWithIndex.sort(
          (a, b) => b.expense.amount - a.expense.amount
        );
      case "lowest":
        return expensesWithIndex.sort(
          (a, b) => a.expense.amount - b.expense.amount
        );
      case "paid":
        return expensesWithIndex.sort((a, b) => {
          if (a.expense.paid === b.expense.paid) return 0;
          return a.expense.paid ? -1 : 1;
        });
      case "unpaid":
        return expensesWithIndex.sort((a, b) => {
          if (a.expense.paid === b.expense.paid) return 0;
          return a.expense.paid ? 1 : -1;
        });
      default:
        return expensesWithIndex;
    }
  };

  const sortedExpenses = getSortedExpenses();

  const exportToPDF = async () => {
    if (hasNoEvents) return;
    if (expenses.length === 0) {
      setError("Nema troškova za izvoz");
      return;
    }

    try {
      const [{ jsPDF, autoTable }] = await Promise.all([loadPDF(), loadPDFLogo()]);

      const doc = new jsPDF();
      const date = new Date().toLocaleDateString("sr-RS");
      let pageNum = 1;

      const startPage = (isFirst = false) => {
        drawPDFHeader(doc, "Pregled troskova", `Datum: ${date}`);
        if (isFirst) return;
      };

      startPage(true);
      let yPosition = 48;

      // ── Stat chips row ────────────────────────────────────────────────────
      yPosition = drawStatRow(doc, [
        { label: "Budžet",           value: `${budgetValue.toFixed(2)} KM` },
        { label: "Troškovi",         value: `${totalExpenses.toFixed(2)} KM` },
        { label: "Plaćeno",          value: `${totalPaid.toFixed(2)} KM` },
        { label: "Neplaćeno",        value: `${totalUnpaid.toFixed(2)} KM` },
        { label: "Preostalo",        value: `${remainingBudget.toFixed(2)} KM` },
      ], yPosition);

      yPosition += 4;

      // ── Expenses table ────────────────────────────────────────────────────
      const tableData = expenses.map((expense) => [
        fixSerbianText(expense.name || ""),
        `${expense.amount.toFixed(2)} KM`,
        expense.paid ? "Da" : "Ne",
      ]);

      const base = getPDFTableStyles();
      const relaxed = withPDFRelaxedRows(base);
      autoTable(doc, {
        ...base,
        ...relaxed,
        startY: yPosition,
        head: [[fixSerbianText("Naziv troska"), "Iznos", fixSerbianText("Placeno")]],
        body: tableData,
        columnStyles: {
          0: { cellWidth: 120, halign: "left", valign: "middle" },
          1: { cellWidth: 35, halign: "left", valign: "middle" },
          2: { cellWidth: 25, halign: "left", valign: "middle" },
        },
        didParseCell: (data: any) => {
          if (data.section === "body" && data.column.index === 2) {
            const paid = data.cell.raw === "Da";
            data.cell.styles.fontStyle = "normal";
            data.cell.styles.textColor = paid ? C.dark : C.gray;
          }
        },
        didDrawPage: (data: any) => {
          if (data.pageNumber > 1) {
            drawPDFHeader(doc, "Pregled troskova", `Datum: ${date}`);
          }
          drawPDFFooter(doc, pageNum + data.pageNumber - 1);
        },
      } as any);

      // Final footer already drawn by didDrawPage; add extra pages count if needed
      const totalPages = (doc as any).lastAutoTable.pageCount ?? 1;
      void totalPages;

      doc.save(`Pregled-troskova-${new Date().toISOString().split("T")[0]}.pdf`);
    } catch (error) {
      console.error("Error exporting PDF:", error);
      setError("Greška pri konvertovanju u PDF");
    }
  };


  if (authLoading || loading) {
    return (
      <EventDataLoading className="min-h-screen flex flex-col items-center justify-center gap-8 px-3 py-4 bg-gradient-to-br from-background to-muted" />
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen px-3 py-4 pb-20 lg:pb-4">
      <div className="max-w-4xl mx-auto space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold">Budžet</h1>
            <p className="text-muted-foreground mt-1">
              Upravljajte budžetom i troškovima za vašu svadbu
            </p>
            {hasNoEvents && (
              <PlannerEventRequiredNotice message="Da bi koristili kalkulator budžeta, morate prvo kreirati proslavu na profilu." />
            )}
          </div>
          {expenses.length > 0 && (
            <Button
              onClick={exportToPDF}
              variant="outline"
              className="w-full sm:w-auto"
              disabled={hasNoEvents}
            >
              <Download className="mr-2 h-4 w-4" />
              Konvertuj u PDF
            </Button>
          )}
        </div>

        {error && (
          <div className="bg-destructive/10 text-destructive p-4 rounded-lg">
            {error}
          </div>
        )}

        {/* Budget Input and Summary - Side by Side on Desktop */}
        <div className="grid gap-4 md:grid-cols-2">
          {/* Budget Input */}
          <Card>
            <CardHeader>
              <CardTitle>Budžet</CardTitle>
              <CardDescription>
                Unesite ukupan budžet za vašu svadbu
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="budget">Iznos budžeta (KM)</Label>
                  <Input
                    id="budget"
                    type="number"
                    step="0.01"
                    min="0"
                    value={budgetAmount}
                    onChange={(e) => setBudgetAmount(e.target.value)}
                    placeholder="0.00"
                    className="mt-2 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    disabled={hasNoEvents}
                  />
                </div>
                <div className="sm:flex sm:justify-end">
                  <Button
                    onClick={handleSaveBudget}
                    disabled={hasNoEvents || saving}
                    className="flex w-full sm:w-fit"
                  >
                    <Save className="mr-2 h-4 w-4" />
                    Sačuvaj budžet
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Budget Summary */}
          <Card>
            <CardHeader>
              <CardTitle>Pregled budžeta</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Ukupan budžet</span>
                  <span className="font-semibold">
                    {budgetValue.toLocaleString("sr-RS")} KM
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Ukupni troškovi</span>
                  <span className="font-semibold">
                    {totalExpenses.toLocaleString("sr-RS")} KM
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Plaćeno</span>
                  <span className="font-semibold text-[#065F46] dark:text-[#D1FAE5]">
                    {totalPaid.toLocaleString("sr-RS")} KM
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Neplaćeno</span>
                  <span className="font-semibold text-orange-600">
                    {totalUnpaid.toLocaleString("sr-RS")} KM
                  </span>
                </div>
                <div className="flex justify-between pt-2 border-t">
                  <span className="font-semibold">Preostali budžet</span>
                  <span
                    className={`font-bold text-lg ${
                      remainingBudget >= 0
                        ? "text-[#065F46] dark:text-[#D1FAE5]"
                        : "text-red-600"
                    }`}
                  >
                    {remainingBudget.toLocaleString("sr-RS")} KM
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Add Expense Form */}
        <Card>
          <CardHeader>
            <CardTitle>Dodaj trošak</CardTitle>
            <CardDescription>Dodajte novi trošak u listu</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 w-full">
              <div className="flex flex-col sm:flex-row gap-2 w-full">
                <div className="w-full">
                  <Label htmlFor="card-expense-name">Naziv troška *</Label>
                  <Input
                    id="card-expense-name"
                    value={cardExpense.name}
                    onChange={(e) => {
                      setCardExpense({ ...cardExpense, name: e.target.value });
                      setCardExpenseNameError(null);
                    }}
                    placeholder="npr. Fotograf"
                    className="mt-2"
                    disabled={hasNoEvents}
                  />
                  {cardExpenseNameError && (
                    <p className="text-destructive text-sm mt-1">
                      {cardExpenseNameError}
                    </p>
                  )}
                </div>
                <div>
                  <Label htmlFor="card-expense-amount">Iznos (KM) *</Label>
                  <Input
                    id="card-expense-amount"
                    type="number"
                    step="0.01"
                    min="0"
                    value={cardExpense.amount || ""}
                    onChange={(e) => {
                      setCardExpense({
                        ...cardExpense,
                        amount: parseFloat(e.target.value) || 0,
                      });
                      setCardExpenseAmountError(null);
                    }}
                    placeholder="0.00"
                    className="mt-2"
                    disabled={hasNoEvents}
                  />
                  {cardExpenseAmountError && (
                    <p className="text-destructive text-sm mt-1">
                      {cardExpenseAmountError}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="card-expense-paid"
                  checked={cardExpense.paid}
                  disabled={hasNoEvents}
                  onCheckedChange={(checked) =>
                    setCardExpense({ ...cardExpense, paid: checked })
                  }
                />
                <Label htmlFor="card-expense-paid">Plaćeno</Label>
              </div>
              <div className="flex justify-end w-full [&_div]:w-full [&_div]:sm:w-fit">
                <Button
                  onClick={handleAddExpenseFromCard}
                  disabled={hasNoEvents}
                  className="sm:ml-auto flex w-full sm:w-fit"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Dodaj trošak
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Expenses List */}
        {expenses.length > 0 && (
          <Card className="mb-0">
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle>Lista troškova</CardTitle>
                  <CardDescription>
                    {expenses.length}{" "}
                    {expenses.length === 1 ? "trošak" : "troškova"}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Label
                    htmlFor="sort-expenses"
                    className="text-sm whitespace-nowrap"
                  >
                    Sortiraj:
                  </Label>
                  <Select
                    value={sortBy}
                    onValueChange={setSortBy}
                    disabled={hasNoEvents}
                  >
                    <SelectTrigger className="w-full sm:w-[180px]">
                      <SelectValue placeholder="Sortiraj" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Bez sortiranja</SelectItem>
                      <SelectItem value="highest">Najveći iznos</SelectItem>
                      <SelectItem value="lowest">Najmanji iznos</SelectItem>
                      <SelectItem value="paid">Plaćeno</SelectItem>
                      <SelectItem value="unpaid">Neplaćeno</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {sortedExpenses.map(
                  ({ expense, originalIndex }, sortedIndex) => (
                    <div
                      key={`${expense.name}-${expense.amount}-${originalIndex}-${sortedIndex}`}
                      className={`p-4 rounded-lg border ${
                        expense.paid
                          ? "bg-[#4bd968]/40 border-[#4bd968]/70 dark:bg-[#6ee67a]/30 dark:border-[#6ee67a]/50"
                          : "bg-card"
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <Checkbox
                          checked={expense.paid}
                          disabled={hasNoEvents}
                          onCheckedChange={() =>
                            toggleExpensePaid(originalIndex)
                          }
                          className="shrink-0 w-5 h-5"
                        />
                        <div
                          className="flex-1 min-w-0 cursor-pointer select-none"
                          role="button"
                          tabIndex={hasNoEvents ? -1 : 0}
                          onClick={() => {
                            if (!hasNoEvents) void toggleExpensePaid(originalIndex)
                          }}
                          onKeyDown={(e) => {
                            if (
                              (e.key === "Enter" || e.key === " ") &&
                              !hasNoEvents
                            ) {
                              e.preventDefault()
                              void toggleExpensePaid(originalIndex)
                            }
                          }}
                        >
                          <h3
                            className={`font-semibold ${
                              expense.paid
                                ? "line-through text-muted-foreground"
                                : ""
                            }`}
                          >
                            {expense.name}{" "}
                            {expense.amount.toLocaleString("sr-RS")} KM
                          </h3>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Button
                            variant="ghost"
                            size="icon"
                            disabled={hasNoEvents}
                            onClick={() => handleEditExpense(originalIndex)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-10 w-10"
                                disabled={hasNoEvents}
                                onClick={() =>
                                  setDeleteExpenseIndex(originalIndex)
                                }
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>
                                  Potvrdite brisanje
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  Da li ste sigurni da želite da obrišete trošak
                                  "{expense.name}"? Ova akcija se ne može
                                  poništiti.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel
                                  onClick={() => setDeleteExpenseIndex(null)}
                                >
                                  Otkaži
                                </AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() =>
                                    handleDeleteExpense(originalIndex)
                                  }
                                  className="h-10 bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Obriši
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    </div>
                  )
                )}
              </div>
            </CardContent>
          </Card>
        )}
        {/* Single "Dodaj trošak" button at the bottom */}
        <div className="flex justify-center">
          <Dialog
            open={isCreateExpenseDialogOpen}
            onOpenChange={setIsCreateExpenseDialogOpen}
          >
            <DialogTrigger asChild>
              <Button
                onClick={() => {
                  setNewExpense({ name: "", amount: 0, paid: false });
                }}
                variant="default"
                className="w-10 h-10 p-0 mt-6"
                disabled={hasNoEvents}
              >
                <Plus className="h-5 w-5" />
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Dodaj trošak</DialogTitle>
                <DialogDescription>
                  Dodajte novi trošak u listu
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="expense-name">Naziv troška *</Label>
                  <Input
                    id="expense-name"
                    value={newExpense.name}
                    onChange={(e) => {
                      setNewExpense({ ...newExpense, name: e.target.value });
                      setExpenseNameError(null);
                    }}
                    placeholder="npr. Fotograf"
                  />
                  {expenseNameError && (
                    <p className="text-destructive text-sm mt-1">
                      {expenseNameError}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="expense-amount">Iznos (KM) *</Label>
                  <Input
                    id="expense-amount"
                    type="number"
                    step="0.01"
                    min="0"
                    value={newExpense.amount || ""}
                    onChange={(e) => {
                      setNewExpense({
                        ...newExpense,
                        amount: parseFloat(e.target.value) || 0,
                      });
                      setExpenseAmountError(null);
                    }}
                    placeholder="0.00"
                  />
                  {expenseAmountError && (
                    <p className="text-destructive text-sm mt-1">
                      {expenseAmountError}
                    </p>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="expense-paid"
                    checked={newExpense.paid}
                    onCheckedChange={(checked) =>
                      setNewExpense({ ...newExpense, paid: checked })
                    }
                  />
                  <Label htmlFor="expense-paid">Plaćeno</Label>
                </div>
              </div>
              <DialogFooter className="flex-row justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsCreateExpenseDialogOpen(false);
                    setNewExpense({ name: "", amount: 0, paid: false });
                    setExpenseNameError(null);
                    setExpenseAmountError(null);
                  }}
                >
                  Otkaži
                </Button>
                <Button
                  onClick={async () => {
                    if (await handleAddExpense()) {
                      setIsCreateExpenseDialogOpen(false);
                    }
                  }}
                >
                  Dodaj trošak
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Edit Expense Dialog */}
      <Dialog
        open={isEditExpenseDialogOpen}
        onOpenChange={setIsEditExpenseDialogOpen}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Izmjeni trošak</DialogTitle>
            <DialogDescription>Unesite izmjene za trošak</DialogDescription>
          </DialogHeader>
          {editingExpense && (
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="edit-expense-name">Naziv troška *</Label>
                <Input
                  id="edit-expense-name"
                  value={editingExpense.name}
                  onChange={(e) => {
                    setEditingExpense({
                      ...editingExpense,
                      name: e.target.value,
                    });
                    setExpenseNameError(null);
                  }}
                  className="mt-2"
                />
                {expenseNameError && (
                  <p className="text-destructive text-sm mt-1">
                    {expenseNameError}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="edit-expense-amount">Iznos (KM) *</Label>
                <Input
                  id="edit-expense-amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={editingExpense.amount || ""}
                  onChange={(e) => {
                    setEditingExpense({
                      ...editingExpense,
                      amount: parseFloat(e.target.value) || 0,
                    });
                    setExpenseAmountError(null);
                  }}
                  className="mt-2"
                />
                {expenseAmountError && (
                  <p className="text-destructive text-sm mt-1">
                    {expenseAmountError}
                  </p>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="edit-expense-paid"
                  checked={editingExpense.paid}
                  onCheckedChange={(checked) =>
                    setEditingExpense({
                      ...editingExpense,
                      paid: checked,
                    })
                  }
                />
                <Label htmlFor="edit-expense-paid">Plaćeno</Label>
              </div>
            </div>
          )}
          <DialogFooter className="flex-row justify-end gap-2">
            <Button variant="outline" onClick={handleCancelEditExpense}>
              Otkaži
            </Button>
            <Button onClick={handleSaveEditedExpense}>
              <Save className="mr-2 h-4 w-4" />
              Sačuvaj izmjene
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
